"use client";

import {useEffect, useState, useCallback} from "react";
import {useParams} from "next/navigation";
import Link from "next/link";
import {SandpackProvider, SandpackLayout, SandpackPreview} from "@codesandbox/sandpack-react";
import {Loader2} from "lucide-react";
import { App } from "@/types";

export default function LivePreviewPage() {
  const params = useParams();
  const slug = params.slug as string;
  const [app, setApp] = useState<App | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch app data by slug
  const fetchApp = useCallback(async () => {
    try {
      const response = await fetch(`/api/apps/slug/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`App "${slug}" not found`);
        }
        throw new Error(`Failed to fetch app: ${response.statusText}`);
      }
      const data = await response.json();
      setApp(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching app:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch app");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchApp();
  }, [fetchApp]);

  // Convert files to Sandpack format
  const getSandpackFiles = () => {
    if (!app?.files?.length) return {};

    const files: Record<string, {code: string}> = {};
    app.files.forEach(file => {
      // Remove 'src/' prefix for Sandpack
      const sandpackPath = file.path.startsWith("src/") ? file.path.substring(4) : file.path;
      files[`/${sandpackPath}`] = {code: file.content};
    });
    return files;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-700 text-lg">Loading app...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !app) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">404</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">App Not Found</h1>
          <p className="text-gray-600 mb-4">{error || "The app you're looking for doesn't exist."}</p>
          <Link
            href="/"
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-block"
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const sandpackFiles = getSandpackFiles();
  const hasFiles = Object.keys(sandpackFiles).length > 0;

  // Show loading state while app is still generating
  if (!hasFiles) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-700 text-lg">App is being generated...</p>
          <p className="text-gray-500 text-sm mt-2">Please wait</p>
        </div>
      </div>
    );
  }

  // Render the live preview (exactly like the live layout in [appId]/page.tsx)
  return (
    <div className="h-screen w-full">
      <SandpackProvider
        files={sandpackFiles}
        theme="light"
        template="react"
        options={{
          externalResources: ["https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"],
          autorun: true,
          recompileMode: "immediate",
          recompileDelay: 200,
        }}
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flex: "1 1 0",
          minHeight: 0,
          overflow: "hidden",
        }}>
        <SandpackLayout
          style={
            {
              height: "100%",
              width: "100%",
              border: "none",
            } as React.CSSProperties
          }>
          <SandpackPreview
            style={{
              height: "100%",
              width: "100%",
            }}
            showNavigator={false}
            showOpenInCodeSandbox={false}
            showRefreshButton={false}
          />
        </SandpackLayout>
      </SandpackProvider>
    </div>
  );
}